import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';

import PaginatedStoreGrid from './PaginatedStoreGrid';
import CategoryFilterPills from './CategoryFilterPills';

import { API_BASE, imageUrl } from '../apiConfig';
const STORE_PAGE_NAV_HEIGHT = '56px';
const MOBILE_BREAKPOINT = 768;
const MOBILE_COLLAPSED_HEIGHT = 56;
const MOBILE_TABS_HEIGHT = 44;
const MOBILE_FIXED_TOTAL = MOBILE_COLLAPSED_HEIGHT + MOBILE_TABS_HEIGHT;

const StorePage = () => {
  const { city, storeName } = useParams();
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeItemType, setActiveItemType] = useState(null);
  const [itemTypeCounts, setItemTypeCounts] = useState({
    product: 0,
    service: 0,
    experience: 0
  });
  const [categoryCounts, setCategoryCounts] = useState({ categories: [], uncategorized_count: 0 });
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get('category');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const fetchHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE}/stores/${storeName}`, { headers: fetchHeaders })
      .then(response => {
        if (!response.ok) throw new Error('Failed to load store information');
        return response.json();
      })
      .then(data => {
        let socialMedia = data.social_media;
        if (typeof socialMedia === 'string') {
          try {
            socialMedia = JSON.parse(socialMedia);
          } catch (e) {
            socialMedia = {};
          }
        } else if (!socialMedia) {
          socialMedia = {};
        }
        const transformedData = {
          ...data,
          phone: data.phone_number,
          email: data.contact_email,
          instagram: socialMedia.instagram || null,
          facebook: socialMedia.facebook || null,
          twitter: socialMedia.twitter || null,
          whatsapp: socialMedia.whatsapp || null
        };
        setStoreInfo(transformedData);
        setLoading(false);
      })
      .catch(err => {
        setError('Unable to load store information');
        setLoading(false);
      });
  }, [storeName]);

  useEffect(() => {
    if (storeName) {
      fetch(`${API_BASE}/stores/${storeName}/item-type-counts`)
        .then(response => {
          if (!response.ok) throw new Error('Failed to load item type counts');
          return response.json();
        })
        .then(data => {
          setItemTypeCounts(data);
          const maxType = Object.entries(data).reduce(
            (max, [type, count]) => count > max.count ? {type, count} : max,
            {type: 'product', count: 0}
          );
          setActiveItemType(maxType.count > 0 ? maxType.type : 'product');
        })
        .catch(() => setActiveItemType('product'));
    }
  }, [storeName]);

  useEffect(() => {
    if (!storeName) return;
    setCategoriesLoading(true);
    fetch(`${API_BASE}/stores/${storeName}/category-counts?item_type=product`)
      .then(response => {
        if (!response.ok) throw new Error('Failed to load category counts');
        return response.json();
      })
      .then(data => {
        setCategoryCounts({
          categories: Array.isArray(data.categories) ? data.categories : [],
          uncategorized_count: data.uncategorized_count || 0,
        });
      })
      .catch(() => {
        setCategoryCounts({ categories: [], uncategorized_count: 0 });
      })
      .finally(() => setCategoriesLoading(false));
  }, [storeName]);

  const getTruncatedDescription = (text) => {
    if (!text) return '';
    const limit = 150;
    return showFullDescription ? text : text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const getImageUrl = imageUrl;
  const ensureProtocol = (url) => {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const formatInstagramHandle = (handle) => {
    if (!handle) return '';
    handle = handle.trim().replace(/^@/, '');
    if (handle.includes('instagram.com/')) {
      const match = handle.match(/instagram\.com\/([^\/\?]+)/);
      return match ? match[1] : handle;
    }
    return handle;
  };

  const getItemTypeLabel = (type) => {
    switch(type) {
      case 'product': return 'Products';
      case 'service': return 'Services';
      case 'experience': return 'Experiences';
      default: return 'Products';
    }
  };

  const handleTabChange = (type) => setActiveItemType(type);

  const handleShare = async () => {
    const shareData = {
      title: storeInfo?.name || 'StoreToyou',
      url: window.location.href,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // User cancelled or share failed; swallow silently.
      }
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied');
      } catch (e) {
        // Clipboard denied; silently ignore.
      }
    }
  };

  const onCategoryChange = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('category', slug);
    else next.delete('category');
    setSearchParams(next);
  };

  // Compose the pill list: alphabetical named categories first, then
  // an Uncategorized entry only when the server surfaced a count (>5 rule).
  const pillCategories = [
    ...categoryCounts.categories.map(c => ({ slug: c.slug, name: c.name, count: c.count })),
    ...(categoryCounts.uncategorized_count > 0
      ? [{ slug: 'uncategorized', name: 'Uncategorized', count: categoryCounts.uncategorized_count }]
      : []),
  ];
  const showCategoryPills = !categoriesLoading && pillCategories.length >= 2;

  const storeLogoSize = 120;

  const styles = {
    page: {
      minHeight: '100dvh',
      background: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '--store-page-nav-height': STORE_PAGE_NAV_HEIGHT,
      // Disable scroll anchoring on this route. The page goes through a
      // multi-stage async load (item-type-counts, storeInfo, categoryCounts,
      // products) where each resolution expands content in the upper region
      // (storeHeader description/contact links, CategoryFilterPills). On
      // short final documents (stores with few products) the browser's
      // default scroll anchoring drags scrollY toward maxScroll across
      // those shifts, landing the user at the footer. ScrollToTop already
      // pinned scrollY=0 before first paint; this keeps it there.
      overflowAnchor: 'none',
    },
    headerContainer: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
    },
    mobileHeaderContainer: {
      transition: 'all 0.3s ease',
    },
    mainHeader: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #e8e8e8',
      padding: scrolled ? '0.5rem 1rem' : '0.75rem 1rem',
      transition: 'all 0.3s ease',
    },
    mobileMainHeader: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #e8e8e8',
      padding: '0.75rem 1rem',
      transition: 'all 0.3s ease',
    },
    navContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: scrolled ? '1.1rem' : '1.25rem',
      fontWeight: '700',
      color: '#FF385C',
      textDecoration: 'none',
      transition: 'font-size 0.3s ease',
    },
    navLinks: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    navLink: {
      textDecoration: 'none',
      color: '#333',
      fontWeight: '500',
      fontSize: scrolled ? '0.85rem' : '0.95rem',
      transition: 'all 0.2s',
    },
    storeHeader: {
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #e8e8e8',
      padding: '1rem',
    },
    storeContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
    storeLogo: {
      width: `${storeLogoSize}px`,
      height: `${storeLogoSize}px`,
      objectFit: 'contain',
      borderRadius: '8px',
      background: '#f8f9fa',
      marginBottom: '0.75rem',
    },
    storeName: {
      fontSize: '1.75rem',
      fontWeight: '600',
      marginBottom: '0.25rem',
    },
    storeLocation: {
      fontSize: '1rem',
      color: '#666',
      marginBottom: '0.5rem',
    },
    storeDescription: {
      fontSize: '0.9rem',
      color: '#666',
      margin: '0.5rem 0',
      maxWidth: '90%',
    },
    contactLinks: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    contactLink: {
      fontSize: '0.85rem',
      color: '#333',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    contentWrapper: {
      paddingTop: isMobile ? 0 : 'var(--store-page-nav-height)',
      marginTop: isMobile && scrolled ? `${MOBILE_FIXED_TOTAL}px` : 0,
      transition: 'margin-top 0.3s ease',
      overflowAnchor: 'none',
    },
    tabsContainer: {
      display: 'flex',
      overflowX: 'auto',
      whiteSpace: 'nowrap',
      borderBottom: '1px solid #e8e8e8',
      marginBottom: '1rem',
      scrollbarWidth: 'none',
      padding: '0 1rem',
    },
    tab: {
      padding: '0.75rem 1rem',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#666',
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderBottom: '2px solid transparent',
      flexShrink: 0,
    },
    activeTab: {
      color: '#FF385C',
      borderBottom: '2px solid #FF385C',
      fontWeight: '600',
    },
    mobileExpandedHeader: {
      background: 'rgba(248, 249, 250, 0.98)',
      borderBottom: '1px solid #dee2e6',
      padding: '1.25rem 1rem',
    },
    mobileExpandedContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
    mobileExpandedLogo: {
      width: 60,
      height: 60,
      objectFit: 'contain',
      borderRadius: 12,
      background: '#fff',
    },
    mobileExpandedName: {
      fontSize: '1.25rem',
      fontWeight: 600,
      margin: '0.75rem 0 0.25rem',
    },
    mobileExpandedCity: {
      fontSize: '0.9rem',
      color: '#666',
      margin: 0,
    },
    mobileExpandedDescription: {
      fontSize: '0.95rem',
      lineHeight: 1.5,
      color: '#444',
      margin: '1rem 0 0',
      maxWidth: '90%',
    },
    mobileExpandedContacts: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginTop: '1rem',
      fontSize: '0.9rem',
    },
    mobileCollapsedHeader: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      height: MOBILE_COLLAPSED_HEIGHT,
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #e8e8e8',
      padding: '0 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    mobileCollapsedFavicon: {
      width: 32,
      height: 32,
      objectFit: 'contain',
      borderRadius: 6,
      background: '#fff',
      flexShrink: 0,
    },
    mobileCollapsedName: {
      flex: 1,
      fontWeight: 600,
      fontSize: '1rem',
      color: '#222',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    mobileShareButton: {
      width: 44,
      height: 44,
      background: 'transparent',
      border: 'none',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
    },
    mobileFixedTabsExtra: {
      position: 'fixed',
      top: MOBILE_COLLAPSED_HEIGHT,
      left: 0,
      right: 0,
      zIndex: 999,
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #dee2e6',
      height: MOBILE_TABS_HEIGHT,
      marginBottom: 0,
      alignItems: 'center',
    },
  };

  if (!activeItemType) {
    return <div style={{ height: '100dvh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><div className="spinner-border text-primary"></div></div>;
  }

  // Store is not public — show "Temporarily Unavailable"
  if (storeInfo && storeInfo._restricted) {
    return (
      <div style={{ minHeight: '100dvh', background: '#f3f4f6', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <header style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0.75rem 1.5rem' }}>
          <Link to="/" style={{ textDecoration: 'none', color: '#FF385C', fontWeight: 700, fontSize: '1.2rem' }}>StoreToyou</Link>
        </header>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 1.5rem', textAlign: 'center' }}>
          {storeInfo.profile_image_url ? (
            <img
              src={getImageUrl(storeInfo.profile_image_url)}
              alt={storeInfo.name}
              style={{ width: 120, height: 120, borderRadius: 16, objectFit: 'cover', marginBottom: '1.5rem', border: '1px solid #e5e7eb' }}
            />
          ) : (
            <div style={{ width: 120, height: 120, borderRadius: 16, background: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: '#d1d5db', marginBottom: '1.5rem' }}>
              {storeInfo.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>{storeInfo.name}</h1>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', border: '1px solid #e5e7eb', maxWidth: 420 }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Temporarily Unavailable</div>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>This store is not currently available. Please check back later.</p>
          </div>
          <Link to="/" style={{ marginTop: '2rem', color: '#FF385C', textDecoration: 'none', fontWeight: 500, fontSize: '0.9rem' }}>
            &larr; Back to StoreToyou
          </Link>
        </div>
      </div>
    );
  }

  const emailIcon = (
    <svg width="16" height="16" fill="#FF385C" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
  );
  const websiteIcon = (
    <svg width="16" height="16" fill="#FF385C" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V8h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
  );
  const instagramIcon = (
    <svg width="16" height="16" fill="#FF385C" viewBox="0 0 24 24"><path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 6.25 1.25 1.25 0 0 1 16 5a1.25 1.25 0 0 1 1.25-.5h.15M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/></svg>
  );

  const hasContact = storeInfo && (storeInfo.email || storeInfo.website || storeInfo.instagram);

  return (
    <div style={styles.page}>
      {!isMobile && (
        <div style={styles.headerContainer}>
          <header style={styles.mainHeader}>
            <div style={styles.navContainer}>
              <Link to="/" style={styles.logo}>StoreToyou</Link>
              <nav style={styles.navLinks}>
                <Link to="/" style={styles.navLink}>Home</Link>
                <Link to="/dashboard" style={styles.navLink}>Dashboard</Link>
                <Link to="/login" style={styles.navLink}>Login</Link>
              </nav>
            </div>
          </header>
        </div>
      )}

      {isMobile && (
        <div style={styles.mobileHeaderContainer}>
          <header style={styles.mobileMainHeader}>
            <div style={styles.navContainer}>
              <Link to="/" style={styles.logo}>StoreToyou</Link>
              <nav style={styles.navLinks}>
                <Link to="/" style={styles.navLink}>Home</Link>
                <Link to="/dashboard" style={styles.navLink}>Dashboard</Link>
                <Link to="/login" style={styles.navLink}>Login</Link>
              </nav>
            </div>
          </header>
        </div>
      )}

      {isMobile && scrolled && (
        <header style={styles.mobileCollapsedHeader}>
          {storeInfo?.profile_image_url && !imageError ? (
            <img
              src={getImageUrl(storeInfo.profile_image_url)}
              alt=""
              width={32}
              height={32}
              style={styles.mobileCollapsedFavicon}
              onError={() => setImageError(true)}
            />
          ) : (
            <div style={{ ...styles.mobileCollapsedFavicon, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', color: '#999', fontWeight: 600 }}>
              {storeInfo?.name?.charAt(0)?.toUpperCase() || ''}
            </div>
          )}
          <span style={styles.mobileCollapsedName}>{storeInfo?.name || storeName}</span>
          <button type="button" onClick={handleShare} aria-label="Share" style={styles.mobileShareButton}>
            <i className="bi bi-share" style={{ fontSize: '1.25rem', color: '#333' }} />
          </button>
        </header>
      )}

      <main style={styles.contentWrapper}>
        {isMobile && (
          <section style={styles.mobileExpandedHeader}>
            <div style={styles.mobileExpandedContent}>
              {loading ? (
                <div style={{ width: 60, height: 60, background: '#eee', borderRadius: 12 }} />
              ) : storeInfo?.profile_image_url && !imageError ? (
                <img
                  src={getImageUrl(storeInfo.profile_image_url)}
                  alt={`${storeInfo.name} logo`}
                  width={60}
                  height={60}
                  style={styles.mobileExpandedLogo}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div style={{ ...styles.mobileExpandedLogo, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', color: '#ccc' }}>
                  {storeInfo?.name?.charAt(0)?.toUpperCase() || ''}
                </div>
              )}
              <h1 style={styles.mobileExpandedName}>{storeInfo?.name || storeName}</h1>
              <p style={styles.mobileExpandedCity}>{storeInfo?.city || city}</p>
              {storeInfo?.description && (
                <p style={styles.mobileExpandedDescription}>
                  {getTruncatedDescription(storeInfo.description)}
                  {storeInfo.description.length > 150 && (
                    <button type="button" onClick={() => setShowFullDescription(!showFullDescription)} style={{ background: 'none', border: 'none', color: '#FF385C', cursor: 'pointer', padding: 0 }}>
                      {showFullDescription ? ' Less' : ' More'}
                    </button>
                  )}
                </p>
              )}
              {hasContact && (
                <div style={styles.mobileExpandedContacts}>
                  {storeInfo.email && (
                    <a href={`mailto:${storeInfo.email}`} style={styles.contactLink}>
                      {emailIcon}
                      Email
                    </a>
                  )}
                  {storeInfo.website && (
                    <a href={ensureProtocol(storeInfo.website)} target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                      {websiteIcon}
                      Website
                    </a>
                  )}
                  {storeInfo.instagram && (
                    <a href={`https://instagram.com/${formatInstagramHandle(storeInfo.instagram)}`} target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                      {instagramIcon}
                      Instagram
                    </a>
                  )}
                </div>
              )}
              {error && <div style={{ color: 'red', marginTop: '0.5rem' }}>{error}</div>}
            </div>
          </section>
        )}
        {!isMobile && (
          <section style={styles.storeHeader}>
            <div style={styles.storeContent}>
              {loading ? (
                <div style={{ width: `${storeLogoSize}px`, height: `${storeLogoSize}px`, background: '#eee', borderRadius: '8px', marginBottom: '0.75rem' }} />
              ) : storeInfo?.profile_image_url && !imageError ? (
                <img
                  src={getImageUrl(storeInfo.profile_image_url)}
                  alt={`${storeInfo.name} logo`}
                  width={storeLogoSize}
                  height={storeLogoSize}
                  style={styles.storeLogo}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div style={{ ...styles.storeLogo, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: '#ccc' }}>
                  {storeInfo?.name?.charAt(0)?.toUpperCase() || ''}
                </div>
              )}
              <h1 style={styles.storeName}>{storeInfo?.name || storeName}</h1>
              <p style={styles.storeLocation}>{storeInfo?.city || city}</p>
              {storeInfo?.description && (
                <p style={styles.storeDescription}>
                  {getTruncatedDescription(storeInfo.description)}
                  {storeInfo.description.length > 150 && (
                    <button onClick={() => setShowFullDescription(!showFullDescription)} style={{ background: 'none', border: 'none', color: '#FF385C', cursor: 'pointer' }}>
                      {showFullDescription ? ' Less' : ' More'}
                    </button>
                  )}
                </p>
              )}
              {hasContact && (
                <div style={styles.contactLinks}>
                  {storeInfo.email && (
                    <a href={`mailto:${storeInfo.email}`} style={styles.contactLink}>
                      {emailIcon}
                      Email
                    </a>
                  )}
                  {storeInfo.website && (
                    <a href={ensureProtocol(storeInfo.website)} target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                      {websiteIcon}
                      Website
                    </a>
                  )}
                  {storeInfo.instagram && (
                    <a href={`https://instagram.com/${formatInstagramHandle(storeInfo.instagram)}`} target="_blank" rel="noopener noreferrer" style={styles.contactLink}>
                      {instagramIcon}
                      Instagram
                    </a>
                  )}
                </div>
              )}
              {error && <div style={{ color: 'red', marginTop: '0.5rem' }}>{error}</div>}
            </div>
          </section>
        )}
        <div
          style={{
            ...styles.tabsContainer,
            ...(isMobile && scrolled ? styles.mobileFixedTabsExtra : {}),
          }}
        >
          {['product', 'service', 'experience'].map(type => (
            <div
              key={type}
              onClick={() => handleTabChange(type)}
              style={{
                ...styles.tab,
                ...(activeItemType === type ? styles.activeTab : {})
              }}
            >
              {getItemTypeLabel(type)} ({itemTypeCounts[type] || 0})
            </div>
          ))}
        </div>
        {activeItemType === 'product' && showCategoryPills && (
          <CategoryFilterPills
            variant="light"
            categories={pillCategories}
            activeSlug={categorySlug}
            onChange={onCategoryChange}
          />
        )}
        <PaginatedStoreGrid
          storeName={storeName}
          itemType={activeItemType}
          categorySlug={activeItemType === 'product' ? categorySlug : null}
          onClearCategory={() => onCategoryChange(null)}
        />
      </main>
    </div>
  );
};

export default StorePage;
